import { Col, Typography } from "antd";
import AppRowContainer from "@lib/components/AppComponents/AppRowContainer";
import { useIntl } from "react-intl";
import CustomPieChart from "@lib/components/MiniComponents/Dashboard/PieChart";
import Revenue from "@lib/components/MiniComponents/Dashboard/Revenue";
import { invoiceStatusColor } from "@lib/helpers/ColorHelper";
import { CurrencyEnum } from "@lib/types/Transaction";
import { memo, useCallback, useMemo } from "react";
import { Dayjs } from "dayjs";
import { useInvoiceQueries } from "@lib/apiQueries";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import { ApiParams } from "@lib/types/Api";
import { useDateRange } from "@lib/hooks/useDateRange";
import {
  PaymentProvidersMiniCard,
  WalletAddressMiniCard,
  ProductMiniCard,
  WhiteLabelMiniCard,
} from "@app/admin/dashboards/components/MiniCards";
import { AppContext } from "@lib/types";
import { DateRangeFilter } from "@lib/components/MiniComponents/Others";
import { SharedBlockchainPaymentList, SharedInvoices } from "@app/shared";
import { StyledFlexDiv } from "@lib/components/StyledComponents";
import { Link } from "react-router-dom";
import { ROUTES } from "@lib/constants";
import _ from "lodash";

function CustomersDashboard() {
  const { messages } = useIntl();
  const { dateBetween, setDateBetween } = useDateRange({
    initialRange: "monthly",
    cache: true,
  });

  const params = useMemo(() => {
    return {
      s: [
        {
          $and: [
            { createdAt: { $gte: dateBetween[0] } },
            { createdAt: { $lte: dateBetween[1] } },
          ],
        },
      ],
    } as ApiParams;
  }, [dateBetween]);

  const handleDateBetween = useCallback((date: [Dayjs, Dayjs]) => {
    setDateBetween(date);
  }, []);

  const { invoicesQuery } = useInvoiceQueries({
    messages,
    queryKey: ["dashboard-invoices", JSON.stringify(params)],
    params,
    enabled: !!params,
  });
  const apiData = invoicesQuery.data?.data?.data;
  const invoiceCurrency = CurrencyEnum.USD;
  const invoiceTotalAmount = useMemo(() => {
    let amount = 0;
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        amount += Number(apiData[index].amount);
      }
    }
    return amount;
  }, [apiData]);
  const invoiceTotalPaid = useMemo(() => {
    let amount = 0;
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        amount += Number(apiData[index].paid);
      }
    }
    return amount;
  }, [apiData]);

  const invoiceStatusGroup = useMemo(() => {
    const data: {
      name: string;
      value: number;
      color: string;
    }[] = [];
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        const item = apiData[index];
        const name =
          (messages["enums.status"][item.status] as string) || item.status;
        const findIndex = data.findIndex((d) => d.name == name);
        if (findIndex == -1) {
          data.push({
            name: name,
            value: 1,
            color: invoiceStatusColor(item.status),
          });
        } else {
          data[findIndex].value++;
        }
      }
    }
    return data.sort((a, b) => b.value - a.value);
  }, [apiData]);

  return (
    <AppsContainer
      title={messages["common.dashboard"] as string}
      fullView
      cardStyle={{
        background: "transparent",
        boxShadow: "none",
      }}
      showDateRange={true}
      dateRangeValue={dateBetween}
      onDateRangeChange={handleDateBetween}
    >
      <AppRowContainer>
        <Col xs={24} md={12} lg={6} xl={6}>
          <WhiteLabelMiniCard cardProps={{ borderFull: true }} />
        </Col>
        <Col xs={24} md={12} lg={6} xl={6}>
          <PaymentProvidersMiniCard cardProps={{ borderFull: true }} />
        </Col>
        <Col xs={24} md={12} lg={6} xl={6}>
          <WalletAddressMiniCard cardProps={{ borderFull: true }} />
        </Col>
        <Col xs={24} md={12} lg={6} xl={6}>
          <ProductMiniCard cardProps={{ borderFull: true }} />
        </Col>

        <Col xs={24} md={24} lg={13}>
          <SharedBlockchainPaymentList
            withoutContainer
            height={220}
            context={AppContext.CUSTOMER}
            tableProps={{
              columnsConfig: {
                noFilter: true,
                noSort: true,
                noPagination: true,
                hiddenColumns: ["txId", "updatedAt", "updatedBy"],
              },
              header: {
                title: (
                  <StyledFlexDiv style={{ gap: 10 }}>
                    <Typography.Text strong>
                      {messages["common.blockchainPayments"] as string}
                    </Typography.Text>

                    <Link to={ROUTES.ADMIN.CUSTOMER.BLOCKCHAIN_PAYMENTS.LIST}>
                      {messages["common.viewAll"] as string}
                    </Link>
                  </StyledFlexDiv>
                ),
              },
              mobileTableProps: {
                hiddenColumns: ["txId"],
                maxHeight: "50vh",
              },
            }}
          />
        </Col>

        <Col xs={24} md={12} lg={7}>
          <CustomPieChart
            title={messages["common.invoices"] as string}
            graph="graph2"
            data={invoiceStatusGroup}
            refetch={invoicesQuery.refetch}
            extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
            cardProps={{
              borderFull: true,
            }}
          />
        </Col>

        <Col xs={24} md={12} lg={4}>
          <Revenue
            title={messages["common.revenue"] as string}
            data={{
              currency: invoiceCurrency,
              paid: invoiceTotalPaid,
              total: invoiceTotalAmount,
            }}
            extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
            cardProps={{
              borderFull: true,
            }}
          />
        </Col>

        <Col xs={24} md={24} lg={24}>
          <SharedInvoices
            withoutContainer
            height={300}
            context={AppContext.CUSTOMER}
            tableProps={{
              showSummary: true, // can be disabled by passing false
              columnsConfig: {
                noFilter: true,
                noSort: true,
                noPagination: true,
                noAction: true,
                hiddenColumns: ["updatedAt", "updatedBy", "createdBy"],
              },
              header: {
                title: (
                  <StyledFlexDiv style={{ gap: 10 }}>
                    <Typography.Text strong>
                      {messages["common.invoices"] as string}
                    </Typography.Text>

                    <Link to={ROUTES.ADMIN.CUSTOMER.INVOICE.LIST}>
                      {messages["common.viewAll"] as string}
                    </Link>
                  </StyledFlexDiv>
                ),
              },
              mobileTableProps: {
                maxHeight: "50vh",
              },
            }}
          />
        </Col>
      </AppRowContainer>
    </AppsContainer>
  );
}

export default memo(CustomersDashboard, (prevProps, nextProps) => {
  return _.isEqual(prevProps, nextProps);
});
